import React, { FC, KeyboardEvent, useState } from "react";
import {
  Box,
  Typography,
  makeStyles,
  createStyles,
  Theme,
  DialogTitle,
} from "@material-ui/core";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./InputOTP";
import { CustomButton } from "./CustomButton";
import { MyDialog } from "./MyDialog";
import { useToastNotifications } from "../../utils/toastUtils";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      // Simulation de l'espacement vertical (space-y-6)
      "& > *:not(:last-child)": {
        marginBottom: theme.spacing(6),
      },
    },
    title: {
      textAlign: "center",
      fontSize: "1rem",
      "& .MuiTypography-h6": {
        fontSize: "1.25rem !important",
      },
    },
    textContainer: {
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      // Simulation de space-y-2
      "& > *:not(:last-child)": {
        marginBottom: theme.spacing(2),
      },
    },
    infoText: {
      fontSize: "0.875rem", // équivalent à text-sm
      color: theme.palette.text.secondary, // simule text-muted-foreground
    },
    highlight: {
      color: theme.palette.primary.main, // simule text-blue-500
    },
    otpWrapper: {
      display: "flex",
      justifyContent: "center",
      overflow: "hidden",
    },
    buttonContainer: {
      display: "flex",
      justifyContent: "center",
      // simulation de gap-2 : marge entre boutons
      "& > *:not(:last-child)": {
        marginRight: theme.spacing(2),
      },
    },
    clickable: {
      cursor: "pointer",
    },
  }),
);

interface OTPFormProps {
  label: string;
  title: string;
  description: React.ReactNode;
  validateLoading?: boolean;
  handleValidate(code: string): void;
  handleClose(): void;
  open: boolean;
  codeLength?: number;
}
const CustomOTPCode: FC<OTPFormProps> = ({
  label,
  description,
  validateLoading,
  handleValidate,
  title,
  open,
  handleClose,
  codeLength = 6,
}: OTPFormProps) => {
  const classes = useStyles();
  const [code, setCode] = useState("");

  const { showErrorToast } = useToastNotifications();

  const handleConfirm = (code: string) => {
    if (code.length < codeLength) {
      showErrorToast({
        title: "Code",
        description: "dialogue.code.send",
      });
    } else {
      handleValidate(code);
    }
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleConfirm(code);
    }
  };

  return (
    <MyDialog
      open={open}
      onClose={handleClose}
      dialogTitle={<DialogTitle className={classes.title}>{title}</DialogTitle>}
      maxWidth="xs"
      fullWidth
    >
      <Box className={classes.root} onKeyPress={handleKeyPress}>
        {/* Bloc d'instructions */}
        {description && (
          <Box className={classes.textContainer}>
            <Typography className={classes.infoText}>{description}</Typography>
          </Box>
        )}

        {/* Zone OTP */}
        <Box className={classes.otpWrapper}>
          <InputOTP
            value={code}
            onChange={(value: string) => {
              setCode(value);
              if (value?.length == codeLength) {
                handleConfirm(value);
              }
            }}
            maxLength={6}
            render={({ slots }: { slots: any }) => (
              <InputOTPGroup>
                {slots.map((slot: any, i: number) => (
                  <InputOTPSlot key={i} index={i} {...slot} />
                ))}
              </InputOTPGroup>
            )}
          />
        </Box>

        {/* Boutons */}
        <Box className={classes.buttonContainer}>
          <CustomButton
            variant="primary"
            label={label}
            onClick={() => handleConfirm(code)}
            loading={validateLoading}
            fullWidthMobile
          />
        </Box>
      </Box>
    </MyDialog>
  );
};

export default CustomOTPCode;
